/**
 * HardWipeCard (iter84.60.4.1)
 *
 * Absolute reset for a single ESPN-sourced tournament.
 *
 * Given a tournament, this card wipes ALL `fixtures` + `match_overrides`
 * for it, then rebuilds fixtures deterministically from the current
 * `upstream_cache` snapshot. No date range — the iter84.60 model treats
 * cache as truth and fixtures as a projection, so partial wipes make no
 * sense. If the cache is wrong, refresh from ESPN first (that appends a
 * new snapshot); THEN run this card.
 *
 * Compare with the older `HardRefreshFromEspnCard` (iter84.59.24), which
 * this card replaces. That one worked on the legacy `scheduled_matches`
 * + `matches` tables and needed a date range because it couldn't rebuild
 * from a snapshot — it re-fetched partial ranges from ESPN live. Under
 * the new model, the cache carries the authoritative view.
 *
 * Flow:
 *   1. Operator picks tournament.
 *   2. Clicks "Preview" → dry-run call → banner shows counts + cache age.
 *   3. Ticks confirmation checkbox acknowledging the destruction.
 *   4. Clicks "Execute Hard Wipe" → wet-run call → success toast.
 *
 * Endpoints:
 *   POST /api/site-admin/tournaments/{tid}/hard-wipe   (dry_run true|false)
 */
import { useState } from "react";
import { AlertTriangle, RefreshCw, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

function formatCacheAge(iso) {
    if (!iso) return "—";
    const then = new Date(iso).getTime();
    if (Number.isNaN(then)) return iso;
    const mins = Math.max(0, Math.floor((Date.now() - then) / 60000));
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
}

export function HardWipeCard({ tournaments = [] }) {
    // iter84.60.5.5 — ESPN + API-Football tournaments are eligible.
    // GotSport migrates in a later iter.
    const eligibleTournaments = tournaments.filter(
        (t) => (t.espn_sport && t.espn_league)
            || (t.api_football_league_id != null && t.api_football_season != null),
    );

    const [tournamentId, setTournamentId] = useState("");
    const [preview, setPreview] = useState(null);
    const [confirmed, setConfirmed] = useState(false);
    const [busy, setBusy] = useState(false);

    const resetPreview = () => {
        setPreview(null);
        setConfirmed(false);
    };

    const runPreview = async () => {
        if (!tournamentId) {
            toast.error("Pick a tournament first.");
            return;
        }
        setBusy(true);
        setConfirmed(false);
        try {
            const p = await api.siteHardWipe(tournamentId, { dryRun: true });
            setPreview(p);
        } catch (err) {
            const msg = err.response?.data?.detail || err.message;
            toast.error(`Preview failed: ${msg}`);
            setPreview(null);
        } finally {
            setBusy(false);
        }
    };

    const executeWipe = async () => {
        if (!preview || !confirmed) return;
        setBusy(true);
        try {
            const result = await api.siteHardWipe(tournamentId, { dryRun: false });
            const stats = result.stats_result || {};
            toast.success(
                `Hard Wipe complete: deleted ${result.fixtures_deleted} fixture(s) + `
                + `${result.overrides_deleted} override(s); rebuilt `
                + `${result.fixtures_rebuilt} fixture(s) from cache; standings `
                + `recomputed for ${stats.teams_updated ?? "?"} team(s).`,
            );
            resetPreview();
        } catch (err) {
            const msg = err.response?.data?.detail || err.message;
            toast.error(`Hard Wipe failed: ${msg}`);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="brutal-card border-2 border-red-500 p-4 space-y-3" data-testid="hard-wipe-card">
            <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-red-400" />
                <h3 className="text-sm font-bold uppercase tracking-widest text-red-400">
                    Hard Wipe (Absolute Reset)
                </h3>
            </div>
            <p className="text-[11px] text-zinc-400 leading-relaxed">
                Deletes every fixture and every override for the chosen tournament, then rebuilds fixtures from the current upstream cache snapshot. Use when the tournament is in an inconsistent state and you want to start clean. No date range — cache is the truth, fixtures are the projection.
            </p>

            <div className="grid grid-cols-1 gap-3">
                <div>
                    <label className="block text-[10px] uppercase text-zinc-500 mb-1">Tournament (ESPN or API-Football)</label>
                    <select
                        data-testid="hard-wipe-tournament-select"
                        value={tournamentId}
                        onChange={(e) => { setTournamentId(e.target.value); resetPreview(); }}
                        className="brutal-input w-full text-xs"
                    >
                        <option value="">— pick a tournament —</option>
                        {eligibleTournaments.map((t) => (
                            <option key={t.id} value={t.id}>{t.name}</option>
                        ))}
                    </select>
                </div>
            </div>

            <div className="flex gap-2">
                <button
                    data-testid="hard-wipe-preview-btn"
                    onClick={runPreview}
                    disabled={busy || !tournamentId}
                    className="brutal-btn text-xs border-zinc-500 hover:bg-zinc-800 disabled:opacity-40"
                >
                    {busy && !preview ? <><Loader2 className="w-3 h-3 animate-spin" /> Previewing…</> : "Preview"}
                </button>
            </div>

            {preview && (
                <div className="brutal-card-light p-3 space-y-2 border-2 border-amber-500" data-testid="hard-wipe-preview-banner">
                    <p className="text-xs font-bold text-amber-400 uppercase">
                        Preview — {preview.tournament_name}
                    </p>
                    <ul className="text-[11px] text-zinc-300 space-y-1">
                        <li>Will delete <b className="text-red-400">{preview.will_delete_fixtures}</b> fixture(s) and <b className="text-red-400">{preview.will_delete_overrides}</b> override(s).</li>
                        <li>Then rebuild fixtures from cache snapshot dated <b className="text-zinc-100">{formatCacheAge(preview.cache_snapshot_at)}</b>.</li>
                        <li className="text-[10px] text-zinc-400 pt-1">
                            Cache snapshot id: <code className="text-zinc-300">{preview.cache_snapshot_id?.slice(0, 8)}…</code>
                        </li>
                    </ul>
                    <p className="text-[11px] text-emerald-400 flex items-center gap-1" data-testid="hard-wipe-safety-line">
                        <ShieldCheck className="w-3 h-3" />
                        Rebuild is deterministic — you can rerun this any time.
                    </p>
                    <label className="flex items-center gap-2 text-[11px] text-zinc-200 pt-1" data-testid="hard-wipe-confirm-checkbox-label">
                        <input
                            type="checkbox"
                            checked={confirmed}
                            onChange={(e) => setConfirmed(e.target.checked)}
                            data-testid="hard-wipe-confirm-checkbox"
                            className="accent-red-500"
                        />
                        <span>I understand this permanently deletes {preview.will_delete_fixtures + preview.will_delete_overrides} row(s) for <b>{preview.tournament_name}</b>.</span>
                    </label>
                    <button
                        data-testid="hard-wipe-execute-btn"
                        onClick={executeWipe}
                        disabled={busy || !confirmed}
                        className="brutal-btn w-full justify-center text-xs bg-red-500/20 border-red-500 text-red-200 hover:bg-red-500/30 disabled:opacity-40"
                    >
                        {busy ? <><Loader2 className="w-3 h-3 animate-spin" /> Wiping…</> : <><RefreshCw className="w-3 h-3" /> Execute Hard Wipe</>}
                    </button>
                </div>
            )}
        </div>
    );
}
